/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type FC, useCallback, useEffect, useState } from 'react';

import { withAnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap, jsx } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { isFedRamp, isIsolatedCloud } from '@atlassian/atl-context';
import { type RecaptchaResult } from '@atlassian/google-recaptcha';
import { getActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';
import { ExperienceSuccess } from '@atlassian/ufo';
import { AnalyticsSource, type Flag, type InvitePeopleProps } from '../../types';
import { INVITE_API_MAX_USERS } from '../../clients';
import InvitePeopleComponent from './InvitePeopleComponent';
import { useAnalytics, qualifiesForViralSettings } from '../../analytics';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import { ProductContext, useProducts } from '../ProductSelect';
import {
	Buggy,
	getFeaturedUsers,
	getUniqueEmailDomains,
	resolveInviteProcessingResponseStatus,
	serializeError,
} from '../../utils';
import { ThirdPartyContext, useThirdPartyState } from '../ThirdParty';
import { InvitePeopleContext, useInvitePeopleContext } from './InvitePeopleContext';
import { CoreInvitesJiraProjectContext } from '../../experiments/coreInvitesInJiraProjects/CoreInvitesJiraProjectContext';
import { InviteeListContext, useInviteeListStateContext } from '../InviteeList';
import Spinner from '@atlaskit/spinner';
import { useViralSettingsFlags } from '../Flags/ViralSettings';
import { InvitePeopleIntlProvider } from '../i18n';
import { ErrorBoundary } from '../ErrorBoundary';
import { ViralSettingsContext, useViralSettingsContext } from '../ViralSettings';
import { startMeasure, stopMeasure } from '../../analytics/performance';
import { invitePeopleRendered } from '../../analytics/ufoExperiences';
import useDirectAccessSettings from './useDirectAccessSettings';
import useOpenInvite from './useOpenInvite';
import useInviteUsers from './useInviteUsers';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { withExperimentalFeatures } from '../../experiments/experimentalFeatures';
import { useInvitePeopleData } from './plugins';
import NoInvitePermission from '../NoInvitePermission/NoInvitePermission';
import { useInfiSpotlight } from '../InfiSpotlight/useInfiSpotlight';
import { useIntl } from 'react-intl-next';
import { getGenericErrorFlag } from '../Flags/InvitePeopleFlag/getFlag';
import { fg } from '@atlaskit/platform-feature-flags';

const styles = cssMap({
	spinnerWrapper: {
		minHeight: '160px',
		marginBlock: token('space.800', '64px'),
		marginInline: token('space.800', '64px'),
		overflowY: 'auto',
	},
});

type InvitePeopleContainerProps = Omit<
	InvitePeopleProps,
	'allowEmail' | 'disableViralSettings' | 'source' | 'viewAs' | 'onInviteeListFocus'
>;

const InvitePeopleContainer: FC<InvitePeopleContainerProps> = ({
	_hasError,
	alignButtons = 'right',
	cancelButtonLabel,
	continueUrl,
	onInviteCompletedHandler,
	applicationEdition,
	experimental_formTitleByline,
	experimental_onReopenHandler,
	formDescription,
	formTitle,
	hideFormHeader: hideFormHeader,
	showProductSelect = true,
	hideCancelButton,
	isInitialLoad,
	maxNumberOfInputs = INVITE_API_MAX_USERS,
	subProduct,
	onCancelHandler,
	onSendHandler,
	onSubmit,
	sendButtonLabel,
	setIsInitialLoad,
	showFlag,
	titleId,
	experimental_submitButtonRef,
	forceLoading,
	experimental_onInviteeListChangeHandler,
	experimental_featuredUsers,
	experimental_productContext,
	experimental_copyLink,
	onAdminMenuItemClick,
}) => {
	const analytics = useAnalytics(InvitePeopleAnalytics);
	const intl = useIntl();
	const invitePeopleData = useInvitePeopleData();
	const { emails, users } = useInviteeListStateContext();
	const {
		disableNoInviteError,
		isLoading: invitePeopleLoading,
		source,
		userRole,
	} = useInvitePeopleContext();

	const {
		availableProductOptions,
		isFetchProductsLoading,
		hasPermissionToInviteOrRequestAccess,
		selectedProducts,
	} = useProducts();

	const [isSendingInvitation, setIsSendingInvitation] = useState<boolean>(false);

	const {
		isOk: thirdPartyOk,
		isLoading: isThirdPartyLoading,
		abortThirdPartyRequests,
	} = useThirdPartyState();

	const { isLoading: isInfiSpotlightLoading } = useInfiSpotlight();

	const {
		viralSettingsDESEligibleStates,
		showOpenInvite,
		openInviteEnabled: enableOpenInvite,
		uniqueEmailDomains,
		setUniqueEmailDomains,
		isViralSettingsEnabled,
	} = useViralSettingsContext();

	const [shouldShowSecondarySection, setShouldShowSecondarySection] = useState(false);

	useEffect(() => {
		invitePeopleRendered.addMetadata({
			source,
			subProduct,
			applicationEdition,
			thirdPartyEnabled: thirdPartyOk,
			isInitialLoad,
		});

		if (setIsInitialLoad && isInitialLoad) {
			setIsInitialLoad(false);
		}
		// calling this effect shouldn't depend on changes to `isInitialLoad`
		// eslint-disable-next-line
	}, [source, subProduct, applicationEdition, thirdPartyOk]);

	useEffect(() => abortThirdPartyRequests, [abortThirdPartyRequests]);

	const onInviteeListChangeCallback = useCallback(() => {
		if (qualifiesForViralSettings(userRole)) {
			setUniqueEmailDomains(getUniqueEmailDomains(emails));
		}
		if (users && users.length > 0) {
			setShouldShowSecondarySection(true);
		}
		// Execute the callback passed by the consumer passing the emails as an argument
		experimental_onInviteeListChangeHandler?.(emails);
	}, [userRole, setUniqueEmailDomains, emails, users, experimental_onInviteeListChangeHandler]);

	const { processDirectAccessSettings } = useDirectAccessSettings({
		viralSettingsDESEligibleStates,
		selectedProducts,
		isViralSettingsEnabled,
	});

	const { processOpenInviteSettings } = useOpenInvite({
		showOpenInvite,
		enableOpenInvite,
		selectedProducts,
		isViralSettingsEnabled,
	});

	const { inviteUsers } = useInviteUsers({
		continueUrl,
		onInviteCompletedHandler,
		onReopenHandler: experimental_onReopenHandler,
	});

	const getViralSettingsFlags = useViralSettingsFlags();

	const [viralSettingsFlags, setViralSettingsFlags] = useState<Flag[]>([]);
	const onSubmitCallback = useCallback(
		async (isSuccessModal: boolean, getRecaptchaToken?: RecaptchaResult['getRecaptchaToken']) => {
			const featuredUsersAnayticsOnSubmit = async () => {
				const featuredUsers = await getFeaturedUsers(experimental_featuredUsers);
				if (featuredUsers && featuredUsers.length > 0) {
					analytics.featuredUsersInviteSucceeded({
						featuredUsersInvited:
							featuredUsers?.filter((featuredUser) =>
								users.some(
									(user) =>
										(user.id && user.id === featuredUser.id) ||
										(user.email && user.email === featuredUser.email),
								),
							).length ?? 0,
						totalUsersInvited: users.length,
					});
				}
			};

			const inviteSuccessContentOnSubmit = async () => {
				const response = await inviteUsers(getRecaptchaToken, true);

				// Displays success and error flags
				if (response.flags && showFlag) {
					[...response.flags, ...viralSettingsFlags].forEach(showFlag);
					onSendHandler?.();
				}
			};

			const invitePeopleOnSubmit = async () => {
				const openInviteSettingsPromise = processOpenInviteSettings();
				const directAccessSettingsPromise = processDirectAccessSettings();
				const inviteUsersPromise = inviteUsers(getRecaptchaToken, false);

				const response = await Promise.allSettled([
					openInviteSettingsPromise,
					directAccessSettingsPromise,
					inviteUsersPromise,
				]);

				const [openInviteResponse, directAccessSettingsResponse, inviteUsersResponse] = response;

				let flags: Flag[] = [];
				if (
					openInviteResponse.status === 'fulfilled' &&
					directAccessSettingsResponse.status === 'fulfilled'
				) {
					flags.push(
						...getViralSettingsFlags(
							resolveInviteProcessingResponseStatus(openInviteResponse.value.responseStatus),
							resolveInviteProcessingResponseStatus(
								directAccessSettingsResponse.value.responseStatus,
							),
						),
					);
					setViralSettingsFlags([...flags]);
				}
				if (inviteUsersResponse.status === 'fulfilled' && inviteUsersResponse.value.flags) {
					flags.push(...inviteUsersResponse.value.flags);
				}

				showFlag && flags.forEach(showFlag);
				onSendHandler?.();

				return {
					openInviteResponse,
					directAccessSettingsResponse,
					inviteUsersResponse,
				};
			};

			try {
				setIsSendingInvitation(true);

				if (experimental_featuredUsers) {
					await featuredUsersAnayticsOnSubmit();
				}

				const internalOnSubmit = isSuccessModal
					? inviteSuccessContentOnSubmit
					: invitePeopleOnSubmit;

				if (onSubmit && !isSuccessModal) {
					await onSubmit({
						defaultOnSubmit: invitePeopleOnSubmit,
						invitePeopleData,
						getRecaptchaToken,
						traceContext: getActiveTrace(),
					});
				} else {
					await internalOnSubmit();
				}
			} catch (error: unknown) {
				const errorMessage = serializeError(error);
				analytics.onSubmitFailed({ isInternal: !onSubmit, isSuccessModal, error: errorMessage });
				if (showFlag) {
					showFlag(getGenericErrorFlag(userRole, intl));
				}
			} finally {
				setIsSendingInvitation(false);
			}
		},
		[
			getViralSettingsFlags,
			viralSettingsFlags,
			invitePeopleData,
			inviteUsers,
			onSendHandler,
			onSubmit,
			processDirectAccessSettings,
			processOpenInviteSettings,
			showFlag,
			analytics,
			experimental_featuredUsers,
			users,
			userRole,
			intl,
		],
	);

	const isFormLoading =
		isThirdPartyLoading || invitePeopleLoading || isFetchProductsLoading || isInfiSpotlightLoading;

	useEffect(() => {
		if (isFormLoading) {
			startMeasure('inviteFormLoad');
		} else {
			stopMeasure('inviteFormLoad', (duration, _) => {
				analytics.inviteFormLoaded(duration, hasPermissionToInviteOrRequestAccess);
			});
			stopMeasure('coreInvitesCompleteLoaded', (duration, _) => {
				analytics.componentCompleteLoaded(duration, hasPermissionToInviteOrRequestAccess);
			});
		}
	}, [analytics, hasPermissionToInviteOrRequestAccess, isFormLoading]);

	const onCancelButtonClick = useCallback(
		async (isSuccessModal?: boolean) => {
			abortThirdPartyRequests();

			if (onCancelHandler) {
				onCancelHandler();
			}

			if (isSuccessModal) {
				showFlag && viralSettingsFlags.forEach(showFlag);
			}
		},
		[abortThirdPartyRequests, onCancelHandler, showFlag, viralSettingsFlags],
	);

	const shouldDisplayNoInvitePermissionMessage =
		!disableNoInviteError && !hasPermissionToInviteOrRequestAccess;

	return (
		<ErrorBoundary
			errorCallback={() => {
				invitePeopleRendered.failure();
			}}
		>
			{isFormLoading || forceLoading ? (
				<Inline
					alignBlock="center"
					alignInline="center"
					testId="testId-invite-people-spinner"
					xcss={styles.spinnerWrapper}
				>
					<Spinner />
				</Inline>
			) : shouldDisplayNoInvitePermissionMessage ? (
				<NoInvitePermission
					onAcknowledgeButtonClick={onCancelButtonClick}
					onCloseButtonClick={onCancelButtonClick}
				/>
			) : (
				<InvitePeopleComponent
					alignButtons={alignButtons}
					cancelButtonLabel={cancelButtonLabel}
					hideCancelButton={hideCancelButton}
					formTitle={formTitle}
					hideFormHeader={hideFormHeader}
					formDescription={formDescription}
					maxNumberOfInputs={maxNumberOfInputs}
					isSendingInvitation={isSendingInvitation}
					onInviteButtonClick={onSubmitCallback}
					onCancelButtonClick={onCancelButtonClick}
					sendButtonLabel={sendButtonLabel}
					productOptions={availableProductOptions}
					onInviteeListChange={onInviteeListChangeCallback}
					showFlag={showFlag}
					showProductSelect={showProductSelect}
					uniqueEmailDomains={uniqueEmailDomains}
					selectedProducts={selectedProducts}
					showSecondarySection={shouldShowSecondarySection}
					titleId={titleId}
					experimental_formTitleByline={experimental_formTitleByline}
					experimental_submitButtonRef={experimental_submitButtonRef}
					experimental_productContext={experimental_productContext}
					experimental_copyLink={experimental_copyLink}
					onAdminMenuItemClick={onAdminMenuItemClick}
				/>
			)}
			{/*we use _hasError to test unhandled error*/}
			{_hasError && <Buggy />}
			{!isFormLoading && <ExperienceSuccess experience={invitePeopleRendered} />}
		</ErrorBoundary>
	);
};

const InvitePeople = withExperimentalFeatures((props: InvitePeopleProps) => {
	const {
		allowEmail,
		enableThirdParty,
		inviteeProvider,
		onCancelHandler,
		orgId,
		plugins,
		productOptions,
		resourceAri,
		showFlag,
		displayInfiSpotlight,
		source,
		subProduct,
		userRole,
		viewAs,
		disableViralSettings = false,
		disableNoInviteError = false,
		onInviteeListFocus,
		initialInvitees,
		experimental_userPickerByline,
		experimental_featuredUsers,
		experimental_productContext,
		experimental_notes,
		inviteeListLabelText,
		inviteeListPlaceholderText,
		inviteeListRequired,
		hideThirdPartyConnectLegend,
		groupByTypeOrder,
		applicationEdition,
		experimental_inlineThirdPartyButtons = false,
		decorateAnalyticsAttributes,
	} = props;
	useUFOComponentExperience(invitePeopleRendered);

	// Disable all third party invites when we are in FedRAMP or Oasis (Isolated Cloud) environment
	let enableThirdPartyProp = isFedRamp() || isIsolatedCloud() ? false : enableThirdParty;

	useEffect(() => startMeasure('coreInvitesCompleteLoaded'), []);

	return (
		<InvitePeopleContext
			inviteeProvider={inviteeProvider}
			orgId={orgId}
			plugins={plugins}
			resourceAri={resourceAri}
			source={source}
			subProduct={subProduct}
			userRole={userRole}
			viewAs={viewAs}
			applicationEdition={applicationEdition}
			disableNoInviteError={disableNoInviteError}
			experimental_featuredUsers={experimental_featuredUsers}
			experimental_inlineThirdPartyButtons={experimental_inlineThirdPartyButtons}
			experimental_productContext={experimental_productContext}
			decorateAnalyticsAttributes={
				fg('jira-1073-additional-attrs') ? decorateAnalyticsAttributes : undefined
			}
			experimental_notes={experimental_notes}
		>
			<InvitePeopleIntlProvider>
				<ThirdPartyContext
					enableThirdParty={enableThirdPartyProp}
					showFlag={showFlag}
					onCancelButtonClick={onCancelHandler}
					displayInfiSpotlight={displayInfiSpotlight}
					hideThirdPartyConnectLegend={hideThirdPartyConnectLegend}
				>
					<CoreInvitesJiraProjectContext>
						<ProductContext resourceAri={resourceAri} productOptions={productOptions}>
							<InviteeListContext
								allowEmail={allowEmail}
								initialInvitees={initialInvitees}
								experimental_userPickerByline={experimental_userPickerByline}
								inviteeListLabelText={inviteeListLabelText}
								inviteeListPlaceholderText={inviteeListPlaceholderText}
								inviteeListRequired={inviteeListRequired}
								groupByTypeOrder={groupByTypeOrder}
								inviteeProvider={inviteeProvider}
							>
								<ViralSettingsContext
									disableViralSettings={disableViralSettings}
									onInviteeListFocus={onInviteeListFocus}
								>
									<InvitePeopleContainer {...props} applicationEdition={applicationEdition} />
								</ViralSettingsContext>
							</InviteeListContext>
						</ProductContext>
					</CoreInvitesJiraProjectContext>
				</ThirdPartyContext>
			</InvitePeopleIntlProvider>
		</InvitePeopleContext>
	);
});

const analyticsContextData = {
	source: AnalyticsSource.ADD_PEOPLE,
	componentName: 'addPeople',
};

export const InvitePeopleWithAnalytics = withAnalyticsContext(analyticsContextData)(InvitePeople);

export default InvitePeople;
